import React, { useState } from 'react';
import { X, Copy, Check, Code, Link as LinkIcon, Share2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import './ShareModal.css';

const ShareModal = ({ isOpen, onClose, videoId, sceneName }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const baseUrl = window.location.origin;
  const embedUrl = `${baseUrl}/embed/${videoId}`;
  const directLink = `${baseUrl}/video/${videoId}`;

  // Generate embed code
  const embedCode = `<iframe
  src="${embedUrl}"
  width="560"
  height="315"
  frameborder="0"
  allowfullscreen
  title="${sceneName}"
></iframe>`;

  // Generate minimal embed code (no footer)
  const minimalEmbedCode = `<iframe
  src="${embedUrl}?minimal=true"
  width="560"
  height="315"
  frameborder="0"
  allowfullscreen
  title="${sceneName}"
></iframe>`;

  // Responsive embed code
  const responsiveEmbedCode = `<div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden;">
  <iframe
    src="${embedUrl}"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
    frameborder="0"
    allowfullscreen
    title="${sceneName}"
  ></iframe>
</div>`;

  const copyToClipboard = async (text, setCopied) => {
    try {
      // Modern clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback for older browsers/mobile
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error('Fallback copy failed:', err);
          alert(t.share?.copyError || 'Failed to copy. Please copy manually.');
        }
        
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      alert(t.share?.copyError || 'Failed to copy. Please copy manually.');
    }
  };

  const handleNativeShare = async () => {
    // Try native share API (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: sceneName || 'AnimLearn Video',
          text: `Check out this video: ${sceneName}`,
          url: directLink
        });
      } catch (err) {
        // User cancelled or error - just ignore
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
          copyToClipboard(directLink, setCopiedLink);
        }
      }
    } else {
      // Fallback to copy
      copyToClipboard(directLink, setCopiedLink);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="share-modal-header">
          <h2 className="share-modal-title">
            {t.share?.title || 'Share Video'}
          </h2>
          <button className="share-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="share-modal-content">
          
          {/* Direct Link */}
          <div className="share-section">
            <div className="share-section-header">
              <LinkIcon size={18} />
              <h3>{t.share?.directLink || 'Direct Link'}</h3>
            </div>
            <p className="share-description">
              {t.share?.directLinkDesc || 'Share this link to view the video on AnimLearn'}
            </p>
            <div className="share-input-group">
              <input
                type="text"
                value={directLink}
                readOnly
                className="share-input"
                onClick={(e) => e.target.select()}
              />
              <button
                className={`share-copy-btn ${copiedLink ? 'copied' : ''}`}
                onClick={() => copyToClipboard(directLink, setCopiedLink)}
              >
                {copiedLink ? <Check size={18} /> : <Copy size={18} />}
                <span>{copiedLink ? (t.copied || 'Copied!') : (t.copy || 'Copy')}</span>
              </button>
            </div>
            
            {/* Native Share Button (Mobile) */}
            {navigator.share && (
              <button 
                className="share-native-btn"
                onClick={handleNativeShare}
              >
                <Share2 size={18} />
                <span>{t.share?.shareButton || 'Share via...'}</span>
              </button>
            )}
          </div>

          {/* Embed Code */}
          <div className="share-section">
            <div className="share-section-header">
              <Code size={18} />
              <h3>{t.share?.embedCode || 'Embed Code'}</h3>
            </div>
            <p className="share-description">
              {t.share?.embedDesc || 'Embed this video on your website with AnimLearn branding'}
            </p>
            <div className="share-input-group">
              <textarea
                value={embedCode}
                readOnly
                className="share-textarea"
                rows={6}
              />
              <button
                className={`share-copy-btn ${copiedEmbed ? 'copied' : ''}`}
                onClick={() => copyToClipboard(embedCode, setCopiedEmbed)}
              >
                {copiedEmbed ? <Check size={18} /> : <Copy size={18} />}
                <span>{copiedEmbed ? (t.copied || 'Copied!') : (t.copy || 'Copy')}</span>
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="share-section">
            <h3 className="share-preview-title">
              {t.share?.preview || 'Preview'}
            </h3>
            <div className="share-preview">
              <iframe
                src={embedUrl}
                width="100%"
                height="315"
                frameBorder="0"
                allowFullScreen
                title={sceneName}
              />
            </div>
          </div>

          {/* Additional Options */}
          <div className="share-options">
            <details className="share-advanced">
              <summary>{t.share?.advancedOptions || 'Advanced Options'}</summary>
              
              {/* Minimal Embed */}
              <div className="share-option">
                <h4>{t.share?.minimalEmbed || 'Minimal Embed (No Footer)'}</h4>
                <textarea
                  value={minimalEmbedCode}
                  readOnly
                  className="share-textarea-small"
                  rows={6}
                />
                <button
                  className="share-copy-btn-small"
                  onClick={() => copyToClipboard(minimalEmbedCode, setCopiedEmbed)}
                >
                  <Copy size={16} />
                  {t.copy || 'Copy'}
                </button>
              </div>

              {/* Responsive Embed */}
              <div className="share-option">
                <h4>{t.share?.responsiveEmbed || 'Responsive Embed (16:9)'}</h4>
                <textarea
                  value={responsiveEmbedCode}
                  readOnly
                  className="share-textarea-small"
                  rows={8}
                />
                <button
                  className="share-copy-btn-small"
                  onClick={() => copyToClipboard(responsiveEmbedCode, setCopiedEmbed)}
                >
                  <Copy size={16} />
                  {t.copy || 'Copy'}
                </button>
              </div>
            </details>
          </div>

          {/* Info */}
          <div className="share-info">
            <p>
              ✅ {t.share?.feature1 || 'Video plays anywhere'}
            </p>
            <p>
              ✅ {t.share?.feature2 || 'AnimLearn branding included'}
            </p>
            <p>
              ✅ {t.share?.feature3 || 'Responsive and mobile-friendly'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
